import { createStore } from 'vuex';
import { httpClient } from '@/shared/services/HttpClient';
import { GlobalState } from '@/shared/models/GlobalState';
import { UserSettings } from './shared/models/UserSettings';
import { setLanguage } from './i18n';
import { localizeCountries } from './shared/constants/mapCountries';

const store = createStore({
  state: {
    status: '',
    user: (window as any).frontUser,
    isMobile: false,
    isMenuOpened: false,
    windowWidth: 0,
    windowHeight: 0,
    adSearchParams: null,
    adSearchViewType: null,
    favoriteCount: (window as any).frontUser ? (window as any).frontUser.favoriteCount : 0,
    compareCount: (window as any).frontUser ? (window as any).frontUser.compareCount : 0,
    showFeedback: (window as any).frontUser ? (window as any).frontUser.showFeedback : false,
    kabernetContext: (window as any).kabernetContext,
    zoomAds: 1,
  } as GlobalState,
  getters: {
    isLoggedIn: (state) => !!(state.user && state.user.userName),
    isEstateAgent: (state) => !!(state.user && state.user.isEstateAgent),
    authStatus: (state) => state.status,
    isMobile: (state) => state.isMobile,
    isMenuOpened: (state) => state.isMenuOpened,
    favoriteCount: (state) => state.favoriteCount,
    compareCount: (state) => state.compareCount,
    showFeedback: (state) => state.showFeedback,
    zoomAds: (state) => state.zoomAds,
    windowWidth: (state) => state.windowWidth,
    windowHeight: (state) => state.windowHeight,
    kabernetContext: (state) => state.kabernetContext,
  },
  mutations: {
    initialiseStore(state) {
      if (localStorage) {
        const adSearchParams = localStorage.getItem('adSearchParams');
        if (adSearchParams) state.adSearchParams = JSON.parse(adSearchParams);
        state.adSearchViewType = localStorage.getItem('adSearchViewType');
        if (localStorage.getItem('showFeedback') === 'false') {
          state.showFeedback = false;
        }
      }
    },
    searchAds(state, payload) {
      if (localStorage) localStorage.setItem('adSearchParams', JSON.stringify(payload));
      state.adSearchParams = payload;
    },
    clearSearchAds(state) {
      if (localStorage) localStorage.removeItem('adSearchParams');
      state.adSearchParams = null;
    },
    searchAdSortBy(state, payload) {
      (state.adSearchParams as any).sortBy = payload;
      if (localStorage) localStorage.setItem('adSearchParams', JSON.stringify(state.adSearchParams));
    },
    searchAdsViewType(state, payload) {
      if (localStorage) localStorage.setItem('adSearchViewType', payload);
      state.adSearchViewType = payload;
    },
    authRequest(state) {
      state.status = 'loading';
    },
    authSuccess(state, user) {
      state.status = 'success';
      state.user = user;
      state.favoriteCount = user.favoriteCount;
      state.compareCount = user.compareCount;
      state.isMenuOpened = false;
      state.showFeedback = user.showFeedback;
    },
    authError(state) {
      state.status = 'error';
    },
    logout(state) {
      state.isMenuOpened = false;
      state.status = '';
      state.user = null;
    },
    resize(state, { width, height }) {
      state.isMobile = width < 1130;
      state.windowWidth = width;
      state.windowHeight = height;
    },
    menuOpened(state, isOpened: boolean) {
      state.isMenuOpened = isOpened;
    },
    userUpdate(state, { firstName, lastName }) {
      if (state.user) {
        state.user.firstName = firstName;
        state.user.lastName = lastName;
        state.user.fullName = firstName ? firstName + ' ' + lastName : lastName;
      }
    },
    confirmEmail(state) {
      if (state.user) {
        state.user.isEmailConfirmed = true;
      }
    },
    userSettingsUpdate(state, settings: UserSettings) {
      if (state.user) {
        state.user.settings = settings;
      }
    },
    favoriteCount(state, favoriteCount) {
      state.favoriteCount = favoriteCount;
    },
    compareCount(state, compareCount) {
      state.compareCount = compareCount;
    },
    showFeedback(state, value) {
      if (localStorage) localStorage.setItem('showFeedback', value);
      state.showFeedback = value;
    },
    zoomAds(state, value) {
      state.zoomAds = value;
    },
  },
  actions: {
    async initApp() {
      const languageId = (window as never)['languageId'] as string;
      await setLanguage(languageId);
      localizeCountries(languageId);
    },
    userUpdate({ commit }, { firstName, lastName }) {
      commit('userUpdate', { firstName, lastName });
    },
    userSettingsUpdate({ commit }, settings: UserSettings) {
      commit('userSettingsUpdate', settings);
    },
    resize({ commit }, { width, height }) {
      commit('resize', { width, height });
    },
    menuOpened({ commit }, isOpened: boolean) {
      commit('menuOpened', isOpened);
    },
    loginForce({ commit }, user) {
      commit('authSuccess', user);
    },
    favoriteCount({ commit }, count) {
      commit('favoriteCount', count);
    },
    compareCount({ commit }, count) {
      commit('compareCount', count);
    },
    showFeedback({ commit }, value) {
      commit('showFeedback', value);
    },
    zoomAds({ commit }, value) {
      commit('zoomAds', value);
    },
    login({ commit }, loginForm) {
      return new Promise((resolve, reject) => {
        commit('authRequest');
        httpClient
          .post('login', loginForm)
          .then((resp) => {
            const user = resp.data;
            commit('authSuccess', user);
            resolve(user);
          })
          .catch((error) => {
            commit('authError');
            reject(error);
          });
      });
    },
    logout({ commit }) {
      return new Promise((resolve, reject) => {
        httpClient
          .post('logout', {})
          .then(() => {
            commit('logout');
            resolve(true);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
  },
  modules: {},
});

export default store;
